import { Monitor } from "lucide-react";
import { Contours } from "@/components/Contours";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Desktop browser window frame for the web version.
 * Pass `src` for a screenshot (ideally 16:10); omit it to render a designed placeholder.
 */
export const BrowserMockup = ({ src, alt = "", label, className = "", testId, loading = "lazy" }) => {
  const { t } = useLanguage();
  return (
    <div data-testid={testId} className={`relative ${className}`}>
      <div className="relative overflow-hidden rounded-2xl bg-[#0b0e0b] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9),0_0_0_1px_rgba(243,241,232,0.09)]">
        {/* title bar */}
        <div className="flex items-center gap-3 border-b border-white/[0.06] bg-[#111612] px-4 py-2.5">
          <div className="flex gap-1.5" aria-hidden="true">
            <span className="h-2.5 w-2.5 rounded-full bg-[#3a3f3a]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#3a3f3a]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#3a3f3a]" />
          </div>
          <div className="mx-auto flex h-6 w-full max-w-xs items-center justify-center rounded-md bg-black/40 font-mono text-[10px] tracking-wider text-[var(--ink-3)]">
            web.huntersmapp.com
          </div>
          <span className="w-10" aria-hidden="true" />
        </div>
        {/* viewport */}
        <div className="relative aspect-[16/10] bg-[#0f130f]">
          {src ? (
            <img src={src} alt={alt} loading={loading} decoding="async" className="h-full w-full object-cover object-top" draggable="false" />
          ) : (
            <div className="relative flex h-full w-full flex-col items-center justify-center bg-gradient-to-b from-[#161c16] via-[#0f140f] to-[#090c09] p-6 text-center">
              <Contours rings={10} step={70} seed={17} cx={700} cy={450} opacity={0.2} />
              <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl border border-[var(--olive)]/40 bg-[var(--olive)]/15">
                <Monitor className="h-6 w-6 text-[var(--sage)]" />
              </div>
              <p className="relative mt-4 font-display text-2xl text-[var(--ink)]">{label}</p>
              <p className="eyebrow relative mt-2 !text-[0.62rem] text-[var(--ink-3)]">
                {t.placeholder.label} · {t.placeholder.note}
              </p>
              {/* faux layout: sidebar + map + table */}
              <div className="relative mt-6 grid w-full max-w-md grid-cols-12 gap-2 opacity-50" aria-hidden="true">
                <div className="col-span-3 space-y-1.5">
                  <div className="h-2 w-full rounded bg-white/10" />
                  <div className="h-2 w-3/4 rounded bg-white/10" />
                  <div className="h-2 w-5/6 rounded bg-white/10" />
                  <div className="h-2 w-2/3 rounded bg-white/10" />
                </div>
                <div className="col-span-5 h-16 rounded-md border border-white/10 bg-white/[0.03]" />
                <div className="col-span-4 space-y-1.5">
                  <div className="h-2 w-full rounded bg-white/10" />
                  <div className="h-2 w-full rounded bg-white/10" />
                  <div className="h-2 w-4/5 rounded bg-white/10" />
                  <div className="h-2 w-full rounded bg-white/10" />
                </div>
              </div>
            </div>
          )}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.015] to-white/[0.05]" />
        </div>
      </div>
    </div>
  );
};
